import React from 'react';
import { HashRouter, Route } from 'react-router-dom';

import _ from 'lodash';

import {
  queryListFacilities,
  queryGetBillingProfile,
  queryCreateBillingProfile,
  queryUpdateBillingProfile
} from '../../../Queries';

import BillingProfile from '../../../Entities/BillingProfile';

import BillingProfileGeneralPanel from './BillingProfileGeneralPanel';
import BillingProfileBillingPanel from './BillingProfileBillingPanel';
import BillingProfileInvoicesPanel from './BillingProfileInvoicesPanel';
import BillingProfilePurchasePanel from './BillingProfilePurchasePanel';
import BillingProfileTerminationPanel from './BillingProfileTerminationPanel';

import PageNavigationLinks from '../../PageNavigationLinks';
import PageNavigationLink from '../../PageNavigationLink';
import ActionButton from '../../Common/ActionButton';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import PageHeader from '../../Common/PageHeader';

import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';
import { getBillingProfileListPageBreadcrumb } from '../BillingProfile/BillingProfileListPage';


const billingProfileQueryFields = [
  'id',
  'facility_id',
  'name',
  'active',
  'validation_only',
  'billing_timing',
  'billing_day',
  'payment_due_day',
  'next_steps',
  'parking_period',
  'mail_invoice_option',
  'termination_notice_day',
  'decline_recurring',
  'provide_survey',
  { 'survey_questions': [
    'id',
    'type',
    'structure',
    'order'
  ] },
  { 'facility': [
    'id',
    'uid',
    'facility_name'
  ] },
  { 'fees': [
    'id',
    'type',
    'price',
    'tax_1',
    'tax_2',
    'tax_3',
    'tax_4',
    'gross_price',
    'start_date'
  ] }
];

const facilityQueryFields = [
  {'facilities': [
  'id',
  'uid',
  'facility_name'
  ]}
];


class BillingProfileDetailPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    billingProfileSaveInProgress: false,
    billingProfileSaveErrors: [],
    billingProfile: null,
    newMode: false,
    editMode: false
  };

  facilityId = null;
  billingProfileId = null;
  staticFacility = false;
  fieldConfiguration = {};
  billingProfilePreEdit = null;


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);

    const billingProfileId = urlSearchParams.get('billingProfile');
    this.billingProfileId = (billingProfileId !== null) ? billingProfileId : null;

    const facilityId = urlSearchParams.get('facility');
    this.facilityId = (facilityId !== null) ? facilityId : null;

    this.staticFacility = (
      billingProfileId !== null || facilityId !== null
    );
  }


  componentDidMount() {
    const initializationTasks = [];

    initializationTasks.push(this.fetchFieldAllowedValues());

    if (this.billingProfileId !== null) {
      initializationTasks.push(this.fetchBillingProfile(this.billingProfileId));
    }
    else {
      initializationTasks.push(new Promise((resolve, reject) => {
        BillingProfile.createFromDefaultValuesForFacility(this.facilityId).then(billingProfile => {
          billingProfile.fields.facility_id.value = this.facilityId;
          resolve(billingProfile);
        }, errors => {
          reject(errors);
        });
      }));
    }

    Promise.all(initializationTasks).then(responses => {
      const billingProfile = responses[(responses.length - 1)];
      const newMode = (this.billingProfileId === null);

      this.setState({
        pageInitializationComplete: true,
        billingProfile,
        newMode
      }, () => {
        if (!window.location.hash || window.location.hash === '#/') {
          window.location.hash = '#/general';
        }
      });
    }, errors => {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors
      });
    });
  }

  fetchFieldAllowedValues() {
    return new Promise((resolve, reject) => {
      const queries = [];

      queries.push(new Promise((resolve, reject) => {
        BillingProfile.getFieldAllowedValues().then(response => {
          if (response.errors) {
            reject(response.errors.map(error => error.message));
            return;
          }

          this.fieldConfiguration = {
            ...this.fieldConfiguration,
            ...response
          };
          resolve();
        }, error => {
          reject([error]);
        });
      }));

      if (this.billingProfileId === null) {
        queries.push(new Promise((resolve, reject) => {
          queryListFacilities(null, facilityQueryFields).then(response => {
            if (response.errors) {
              reject(response.errors.map(error => error.message));
              return;
            }

            const options = response.data.listFacilities.facilities.map(facility => {
              return { value: facility.uid, label: facility.facility_name };
            });
            this.fieldConfiguration = {
              ...this.fieldConfiguration,
              facility: {
                options
              }
            };
            resolve();
          }, error => {
            reject([error]);
          });
        }));
      }

      Promise.all(queries).then(() => {
        resolve();
      }, errors => {
        reject(errors);
      });
    });
  }


  fetchBillingProfile(billingProfileId) {
    return new Promise((resolve, reject) => {
      queryGetBillingProfile({ billing_profile_id: billingProfileId }, billingProfileQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const billingProfile = BillingProfile.createFromQueryValues(response.data.getBillingProfile);

        const facilityOption = {
          value: billingProfile.fields.facility.value.fields.uid.value,
          label: billingProfile.fields.facility.value.fields.facility_name.value
        };
        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          facility: {
            options: [facilityOption]
          }
        };

        resolve(billingProfile);
      }, error => {
        reject([error]);
      });
    });
  }


  saveBillingProfile() {
    const {
      newMode,
      billingProfile
    } = this.state;

    return new Promise((resolve, reject) => {
      this.setState({
        billingProfileSaveInProgress: true,
        billingProfileSaveErrors: []
      }, () => {
        const variables = billingProfile.getQueryFieldValues();

        const query = (newMode) ? queryCreateBillingProfile : queryUpdateBillingProfile;
        query(variables, billingProfileQueryFields).then(response => {
          if (response.errors) {
            const errors = response.errors.map(error => error.message);
            this.setState({
              billingProfileSaveInProgress: false,
              billingProfileSaveErrors: errors
            }, () => {
              reject(errors);
            });
            return;
          }

          const responseKey = Object.keys(response.data)[0];
          const billingProfile = BillingProfile.createFromQueryValues(response.data[responseKey]);

          this.billingProfilePreEdit = _.cloneDeep(billingProfile);

          this.setState({
            billingProfile,
            billingProfileSaveInProgress: false,
            newMode: false
          }, () => {
            resolve();
          });
        }, error => {
          this.setState({
            billingProfileSaveInProgress: false,
            billingProfileSaveErrors: [error]
          }, () => {
            reject([error]);
          });
        });
      });
    });
  }


  storeBillingProfileValue = (inputId, value) => {
    const billingProfile = _.cloneDeep(this.state.billingProfile);

    switch (inputId) {
      case 'billing_timing':
        // Reset the value of `billing_day` and `termination_notice_day`
        // as the allowed values are different depending on the value of
        // `billing_timing`.
        billingProfile.fields.billing_day.value = null;
        billingProfile.fields.termination_notice_day.value = null;
        billingProfile.fields[inputId].value = value;
        break;
      case 'fees':
      case 'survey_questions':
        billingProfile.fields[inputId] = value;
        break;
      case 'validation_only':
        billingProfile.fields[inputId].value = value;
        billingProfile.fields.name.value = value ? 'Validation Only' : '';
        break;
      default:
        billingProfile.fields[inputId].value = value;
    }

    this.setState({ billingProfile });
  }


  enterEditMode() {
    this.billingProfilePreEdit = _.cloneDeep(this.state.billingProfile);
    this.setState({ editMode: true });
  }


  handlePageActionEditButtonClick = () => {
    this.enterEditMode();
  }


  handlePageActionSaveButtonClick = () => {
    this.saveBillingProfile().then(() => {
      // success
    }, error => {
      // error
    });
  }


  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getBillingProfileListPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getBillingProfileDetailPageBreadcrumb());

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const heading = 'Billing Profile';

    return (
      <PageHeader heading={heading} />
    );
  }


  renderPageInitializationProcessing() {
    return (
      <div className="parkchirp--page--user-message-container">
        <ProcessingIndicator userMessage="Initializing, please wait..." />
      </div>
    );
  }


  renderPageInitializationError() {
    const {
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page--user-message-container">
        <div className="parkchirp--page--user-message">
          <p>The following errors occurred while initializing the page:</p>
          <ul>
            {pageInitializationErrors.map(error =>
              <li key={performance.now()}>{error}</li>
            )}
          </ul>
        </div>
      </div>
    );
  }


  renderEditActionBlock() {
    return (
      <div className="parkchirp--sidebar-block">
        <div className="parkchirp--sidebar-block--page-actions">
          <button type="button"
            className="button button-primary button-large page-action"
            onClick={this.handlePageActionEditButtonClick}>
            Edit
          </button>
        </div>
      </div>
    );
  }


  renderSaveActionBlock() {
    const {
      billingProfileSaveInProgress,
      billingProfileSaveErrors
    } = this.state;

    return (
      <div className="parkchirp--sidebar-block">
        <div className="parkchirp--sidebar-block--page-actions">
          <ActionButton label="Save"
            classNames={[ 'page-action' ]}
            onClick={this.handlePageActionSaveButtonClick}
            inProgressState={billingProfileSaveInProgress}
          />
           <a href={`?page=parkchirp_billing_profiles&facility=${this.facilityId}`} style={{marginLeft: '15px'}} >
            Cancel
          </a>
        </div>
        {(billingProfileSaveErrors.length > 0) &&
          <div>
            <p>The following errors occurred while saving the Billing Profile:</p>
            <ul>
              {billingProfileSaveErrors.map(error =>
                <li key={performance.now()}>{error}</li>
              )}
            </ul>
          </div>
        }
      </div>
    );
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors,
      billingProfile,
      newMode,
      editMode
    } = this.state;

    const pageNavigationLinkDisabled = (!pageInitializationComplete);

    return (
      <div className="parkchirp--page parkchirp--page--billing-profile-detail">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page">
            <div className="parkchirp--page-content">
              {(pageInitializationErrors.length === 0) &&
                this.renderPageInitializationProcessing()
              }
              {(pageInitializationErrors.length > 0) &&
                this.renderPageInitializationError()
              }
            </div>
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
          <div className="parkchirp--page-content parkchirp--grid">
            <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
              <HashRouter>
                <PageNavigationLinks>
                  <PageNavigationLink path="/general" label="General" disabled={pageNavigationLinkDisabled} />
                  <PageNavigationLink path="/billing" label="Billing" disabled={pageNavigationLinkDisabled} />
                  <PageNavigationLink path="/invoices" label="Invoices" disabled={pageNavigationLinkDisabled} />
                  <PageNavigationLink path="/purchase" label="Purchase" disabled={pageNavigationLinkDisabled} />
                  <PageNavigationLink path="/termination" label="Termination" disabled={pageNavigationLinkDisabled} />
                </PageNavigationLinks>
                {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
                  <React.Fragment>
                    <Route path="/general" render={props => <BillingProfileGeneralPanel
                      {...props}
                      billingProfile={billingProfile}
                      fieldConfiguration={this.fieldConfiguration}
                      staticFacility={this.staticFacility}
                      onBillingProfileInputChange={this.storeBillingProfileValue}
                      newMode={newMode}
                      editMode={editMode} />}
                    />
                    <Route path="/billing" render={props => <BillingProfileBillingPanel
                      {...props}
                      billingProfile={billingProfile}
                      fieldConfiguration={this.fieldConfiguration}
                      onBillingProfileInputChange={this.storeBillingProfileValue}
                      newMode={newMode}
                      editMode={editMode} />}
                    />
                    <Route path="/invoices" render={props => <BillingProfileInvoicesPanel
                      {...props}
                      billingProfile={billingProfile}
                      fieldConfiguration={this.fieldConfiguration}
                      onBillingProfileInputChange={this.storeBillingProfileValue}
                      newMode={newMode}
                      editMode={editMode} />}
                    />
                    <Route path="/purchase" render={props => <BillingProfilePurchasePanel
                      {...props}
                      billingProfile={billingProfile}
                      fieldConfiguration={this.fieldConfiguration}
                      onBillingProfileInputChange={this.storeBillingProfileValue}
                      newMode={newMode}
                      editMode={editMode} />}
                    />
                    <Route path="/termination" render={props => <BillingProfileTerminationPanel
                      {...props}
                      billingProfile={billingProfile}
                      fieldConfiguration={this.fieldConfiguration}
                      onBillingProfileInputChange={this.storeBillingProfileValue}
                      newMode={newMode}
                      editMode={editMode} />}
                    />
                  </React.Fragment>
                }
              </HashRouter>
            </div>
            <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
              {(!newMode && !editMode) &&
                this.renderEditActionBlock()
              }
              {(newMode || editMode) &&
                this.renderSaveActionBlock()
              }
            </div>
          </div>
        }
      </div>
    );
  }
}
export default BillingProfileDetailPage;


export function getBillingProfileDetailPageBreadcrumb(currentPage = true, billingProfileId = null, facilityId = null) {
  const title = 'Billing Profile';
  const href = (currentPage) ? null :
    `?page=parkchirp_billing_profile${(billingProfileId !== null) ? `&billingProfile=${billingProfileId}` : ''}${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
